import React, { useContext } from 'react';
import { useDeleteQuizQuestionMutation } from '../../services/SkillsManagement';
import { useDispatch } from 'react-redux';
import { DELETE_QUIZ_QUESTION } from '../../Reducers/Actions';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import { MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { SnackBarContext } from '../../Context/SnackBarContextProvider';
import MuiDialog from '../../SystemDesign/MuiDialog';

/**
 * DeleteQuestion Component - Allows deleting a quiz question
 * @param {Object} props
 * @param {Object} props.row - The row or object that contains the question data
 * @param {Object} [props.question] - Alternatively pass the question object directly
 * @param {Function} [props.onDeleted] - Callback invoked after successful deletion
 */
const DeleteQuestion = ({ row, question, onDeleted }) => {
  const [deleteQuestion, { isLoading }] = useDeleteQuizQuestionMutation();
  const dispatch = useDispatch();
  const { setInfoAlert, setOpenSnackBar } = useContext(SnackBarContext);

  const q = question || row?.original || row || {};
  const questionId = Number(q?.questionId ?? q?.id);
  const questionText = q?.questionText || 'this question';

  if (!Number.isFinite(questionId)) return null;

  const handleDelete = async () => {
    try {
      try {
        const res = await deleteQuestion({ id: questionId }).unwrap();
        // update store
        dispatch({ type: DELETE_QUIZ_QUESTION, payload: { questionId } });

        // success message from API or fallback
        const successMessage = typeof res === 'string'
          ? res
          : (res?.message || 'Question deleted successfully');
        setInfoAlert({ severity: 'success', message: successMessage });
        setOpenSnackBar(true);
        onDeleted?.();
        return true;
      } catch (parseErr) {
        if (parseErr?.originalStatus === 200) {
          // treat plain-text 200 as success
          dispatch({ type: DELETE_QUIZ_QUESTION, payload: { questionId } });
          const msg = parseErr?.data || 'Question deleted successfully';
          setInfoAlert({ severity: 'success', message: msg });
          setOpenSnackBar(true);
          onDeleted?.();
          return true;
        }
        console.error('Delete question parse error', parseErr);
        throw parseErr;
      }
    } catch (error) {
      console.error('Delete question error', error);
      const errorMessage =
        error?.data?.message ? String(error.data.message) : (error?.message || 'Failed to delete question');
      setInfoAlert({ severity: 'error', message: errorMessage });
      setOpenSnackBar(true);
      return false;
    }
  };

  const triggerButton = (
    <MenuItem>
      <ListItemIcon>
        <DeleteIcon fontSize="small" color="error" />
      </ListItemIcon>
      <ListItemText>Delete</ListItemText>
    </MenuItem>
  );

  const dialogContent = (
    <div>
      <p>Are you sure you want to delete this question?</p>
      <p><em>{questionText}</em></p>
      <p>This action is irreversible.</p>
    </div>
  );

  return (
    <MuiDialog
      icon={<DeleteOutlineIcon />}
      triggerButton={triggerButton}
      title="Delete"
      subtitle={`You are about to delete a question`}
      content={dialogContent}
      maxWidth="xs"
      existAction={true}
      actionTitle="Delete"
      actionFunction={handleDelete}
      isLoading={isLoading}
    />
  );
};

// For use inside card menus
DeleteQuestion.menuIcon = <DeleteOutlineIcon fontSize="small" />;
DeleteQuestion.menuLabel = 'Delete';

export default DeleteQuestion;

